// routes/clubs/$clubId/posts/$postId.tsx
// -----------------------------------------------------------------------------
// Post detail page - displays a full post with comments and engagement.
// -----------------------------------------------------------------------------

import { useState, useMemo, useRef } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { DateTime } from "luxon";
import { requireAuth } from "@/lib/auth";
import { AppLayout, PageTransition, useHeaderConfig } from "@/components/layout";
import {
  useFetchPostQuery,
  useFetchPostCommentsQuery,
  useFetchUserProfilesByIdsQuery,
  useFetchCurrentUserProfileQuery,
  useFetchClubMembersQuery,
  useCheckClubMembershipQuery,
  useCreateCommentMutation,
  useVotePollMutation,
  useDeletePostMutation,
  useDeleteCommentMutation,
  useTogglePostLikeMutation,
  useToggleCommentLikeMutation,
  useTogglePostPinMutation,
  type Comment,
  type UserProfile,
  type ClubRole,
} from "@/api/clubsApi";
import { isOwnerOrCohost } from "@tinyclub/shared/models";
import { useAuth } from "@/components/providers/AuthProvider";
import { Button } from "@/components/ui/Button";
import { Icon } from "@/components/ui/Icon";
import { Skeleton } from "@/components/ui/Skeleton";
import { WebMediaViewer } from "@/components/ui/WebMediaViewer";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/DropdownMenu";
import { DeleteConfirmDialog } from "@/components/ui/DeleteConfirmDialog";
import { ReportContentDialog } from "@/components/ui/ReportContentDialog";
import { cn } from "@/components/ui/cn";
import { userDisplayName, buildMentionMap } from "@tinyclub/shared/utils";
import { LocationView } from "@/components/location";
import { PollDisplay } from "@/components/posts/PollDisplay";
import { RichBodyText, type MentionMap } from "@/components/ui/RichBodyText";
import { LinkPreview } from "@/components/ui/LinkPreview";
import { GifPreview } from "@/components/ui/GifPreview";
import { MentionTextarea } from "@/components/ui/MentionTextarea";
import { extractFirstUrl, countUrls, isGifUrl } from "@/utils/linkUtils";

export const Route = createFileRoute("/clubs/$clubId_/posts/$postId")({
  beforeLoad: requireAuth,
  component: PostDetailPage,
});

function PostDetailPage() {
  const { clubId, postId } = Route.useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const currentUserId = user?.id;

  const [commentText, setCommentText] = useState("");
  const [isInputExpanded, setIsInputExpanded] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const replyInputRef = useRef<HTMLDivElement>(null);
  const [viewerMedia, setViewerMedia] = useState<{
    src: string;
    type: "image" | "video";
  } | null>(null);

  // Fetch post data
  const { data: post, isLoading: postLoading } = useFetchPostQuery(
    currentUserId ? { postId, userId: currentUserId } : { postId },
    { skip: !postId }
  );

  // Fetch comments
  const { data: comments, isLoading: commentsLoading } = useFetchPostCommentsQuery(
    currentUserId ? { postId, userId: currentUserId } : { postId },
    { skip: !postId }
  );

  // Fetch user's club membership for permission checks
  const { data: membership } = useCheckClubMembershipQuery(
    { clubId, userId: currentUserId || "" },
    { skip: !currentUserId || !clubId }
  );

  // Fetch club members with profiles for mention map
  const { data: members } = useFetchClubMembersQuery({
    clubId,
    includeUserProfiles: true,
  });

  // Build mention map from members
  const mentionMap = useMemo(() => {
    if (!members) return {};
    // Transform web API format (user_profile) to shared format (user_profiles)
    const membersForMention = members.map((m) => ({
      user_id: m.user_id,
      user_profiles: m.user_profile,
    }));
    return buildMentionMap(membersForMention);
  }, [members]);

  // Create comment mutation
  const [createComment, { isLoading: isSubmitting }] = useCreateCommentMutation();

  // Poll voting
  const [votePoll, { isLoading: isVoting }] = useVotePollMutation();

  // Delete post mutation
  const [deletePost, { isLoading: isDeleting }] = useDeletePostMutation();

  // Like post mutation
  const [toggleLike] = useTogglePostLikeMutation();

  // Pin post mutation
  const [togglePin] = useTogglePostPinMutation();

  // Like comment mutation
  const [toggleCommentLike] = useToggleCommentLikeMutation();

  // Permission checks
  const userRole = membership?.role;
  const isPostAuthor = currentUserId && post?.author_id === currentUserId;
  const isAdminOrManager = userRole === "owner" || userRole === "cohost";
  const canDelete = isPostAuthor || isAdminOrManager;
  const canEdit = isPostAuthor;
  const showOverflowMenu = currentUserId;

  // Handle delete
  const handleDelete = async () => {
    try {
      await deletePost({ postId, clubId }).unwrap();
      setShowDeleteDialog(false);
      // Navigate back to club page
      navigate({ to: "/clubs/$identifier", params: { identifier: clubId } });
    } catch (error) {
      console.error("Failed to delete post:", error);
    }
  };

  const handlePinToggle = async () => {
    try {
      await togglePin({ postId, clubId });
    } catch (error) {
      console.error("Failed to toggle pin:", error);
    }
  };

  const handlePollVote = async (optionId: string) => {
    if (!post?.poll || !currentUserId) return;

    try {
      await votePoll({
        pollId: post.poll.id,
        optionId,
        postId: post.id,
        clubId,
        previousOptionId: post.poll.user_vote_option_id,
        userId: currentUserId,
      });
    } catch (error) {
      console.error("Failed to vote on poll:", error);
    }
  };

  const handleLikeToggle = async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!currentUserId || !post) return;

    try {
      await toggleLike({
        postId: post.id,
        clubId,
        userId: currentUserId,
      });
    } catch (error) {
      console.error("Failed to toggle like:", error);
    }
  };

  const handleCommentLike = async (commentId: string) => {
    if (!currentUserId) return;

    try {
      await toggleCommentLike({
        commentId,
        postId,
        userId: currentUserId,
      });
    } catch (error) {
      console.error("Failed to toggle comment like:", error);
    }
  };

  // Fetch current user's profile for avatar
  const { data: currentUserProfile } = useFetchCurrentUserProfileQuery(
    currentUserId ?? "",
    { skip: !currentUserId }
  );
  const currentUserAvatar = currentUserProfile?.avatar_small || currentUserProfile?.avatar_medium;

  // Collect user IDs for profile lookup
  const userIds = new Set<string>();
  if (post?.author_id) userIds.add(post.author_id);
  comments?.forEach((c) => {
    if (c.user_id) userIds.add(c.user_id);
  });

  // Fetch user profiles
  const { data: profiles } = useFetchUserProfilesByIdsQuery(
    Array.from(userIds),
    { skip: userIds.size === 0 }
  );

  // Create a map for easy lookup
  const profileMap = new Map<string, UserProfile>();
  profiles?.forEach((p) => profileMap.set(p.id, p));

  const author = post?.author_id ? profileMap.get(post.author_id) : null;

  // Get media (moved before early returns to keep hooks order consistent)
  const firstMedia = post?.post_media?.[0];
  const hasImage = firstMedia?.kind === "image";
  const hasVideo = firstMedia?.kind === "video";

  // Determine if we should show link preview or GIF preview (must be before early returns)
  const { firstUrl, showGifPreview, showLinkPreview } = useMemo(() => {
    const bodyText = post?.body_text || "";
    const firstUrl = extractFirstUrl(bodyText);
    const urlCount = countUrls(bodyText);
    const isGif = isGifUrl(firstUrl);

    // Show GIF preview if there's exactly 1 URL and it's a GIF
    const showGifPreview = urlCount === 1 && isGif && firstUrl !== null;

    // Show link preview if there's exactly 1 URL and it's NOT a GIF and no media attached
    const showLinkPreview = urlCount === 1 && !isGif && firstUrl !== null && !hasImage && !hasVideo;

    return { firstUrl, showGifPreview, showLinkPreview };
  }, [post?.body_text, hasImage, hasVideo]);

  // Set header configuration (same pattern as other pages)
  useHeaderConfig(
    {
      breadcrumbs: [
        { label: "Club", path: `/clubs/${clubId}` },
        { label: "Post" },
      ],
    },
    [clubId]
  );

  // Handle comment submission
  const handleSubmitComment = async () => {
    if (!commentText.trim() || !currentUserId || !postId) return;

    try {
      await createComment({
        post_id: postId,
        user_id: currentUserId,
        body_text: commentText.trim(),
      }).unwrap();
      setCommentText("");
    } catch {
      // Error handling - could show toast
      console.error("Failed to create comment");
    }
  };

  if (postLoading) {
    return (
      <AppLayout>
        <PageTransition>
          <PostDetailSkeleton />
        </PageTransition>
      </AppLayout>
    );
  }

  if (!post) {
    return (
      <AppLayout>
        <PageTransition>
          <div className="p-6">
            <div className="max-w-2xl mx-auto bg-surface-1 rounded-lg border border-border-tertiary p-8 text-center">
              <Icon name="message" size={48} className="text-text-tertiary mx-auto mb-4" />
              <h2 className="text-body-1-medium text-text-primary">Post not found</h2>
              <p className="text-body-2 text-text-secondary mt-2">
                This post may have been deleted or you don't have access to it.
              </p>
              <Link to="/clubs/$identifier" params={{ identifier: clubId }}>
                <Button variant="outline" className="mt-4">
                  Back to Club
                </Button>
              </Link>
            </div>
          </div>
        </PageTransition>
      </AppLayout>
    );
  }

  const authorName = author ? userDisplayName(author) : "Unknown";
  const authorInitials = getInitials(authorName);
  const authorAvatar = author?.avatar_small || author?.avatar_medium;
  const formattedTime = formatRelativeTime(post.created_at);

  // Get media URLs (firstMedia, hasImage, hasVideo already defined above)
  const imageUrl =
    firstMedia?.image_medium || firstMedia?.image_full || firstMedia?.image_small;
  const fullImageUrl = firstMedia?.image_full || firstMedia?.image_medium;
  const videoUrl = firstMedia?.video_url;

  // Handle media click
  const handleMediaClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (hasVideo && videoUrl) {
      setViewerMedia({ src: videoUrl, type: "video" });
    } else if (hasImage && fullImageUrl) {
      setViewerMedia({ src: fullImageUrl, type: "image" });
    }
  };

  return (
    <AppLayout>
      <PageTransition>
        {/* Content */}
        <div className="p-6">
          <div className="max-w-2xl mx-auto space-y-6">
            {/* Post Card - matching mobile PostCard layout exactly */}
            <div
              className={cn(
                "px-5 py-4",
                post.pinned && "bg-surface-1"
              )}
            >
              {/* Pinned indicator */}
              {post.pinned && (
                <div className="flex items-center gap-1.5 text-body-3 text-text-tertiary mb-3">
                  <Icon name="thumbtack-outline" size={12} />
                  <span>Pinned</span>
                </div>
              )}

              {/* Two-column layout matching mobile exactly */}
              <div className="flex">
                {/* Left column - Avatar only, 10px margin right */}
                <div className="mr-2.5 flex-shrink-0">
                  {/* Avatar - explicit 32x32px to match mobile */}
                  <div
                    className="rounded-full overflow-hidden bg-surface-2 flex-shrink-0"
                    style={{ width: 32, height: 32 }}
                  >
                    {authorAvatar ? (
                      <img
                        src={authorAvatar}
                        alt={authorName}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-xs font-medium text-text-secondary">
                        {authorInitials}
                      </div>
                    )}
                  </div>
                </div>

                {/* Right column - All content */}
                <div className="flex-1 min-w-0">
                  {/* Author info, timestamp, and overflow menu */}
                  <div className="flex items-baseline gap-1.5 mb-1.5">
                    <span className="font-medium text-body-2 text-text-primary truncate">
                      {authorName}
                    </span>
                    <span className="text-body-3 text-text-tertiary flex-shrink-0">
                      {formattedTime}
                    </span>
                    {/* Spacer to push overflow menu to right */}
                    <div className="flex-1" />
                    {/* Overflow menu */}
                    {showOverflowMenu && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className="p-1 -mr-1 text-text-tertiary hover:text-text-secondary rounded-lg hover:bg-surface-2 transition-colors"
                            aria-label="Post options"
                          >
                            <Icon name="ellipsis" size={16} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {canEdit && (
                            <DropdownMenuItem
                              onClick={() => {
                                // TODO: Implement edit functionality
                                console.log("Edit post:", post.id);
                              }}
                            >
                              <Icon name="pen" size={14} />
                              Edit
                            </DropdownMenuItem>
                          )}
                          {isAdminOrManager && (
                            <DropdownMenuItem onClick={handlePinToggle}>
                              <Icon name={post.pinned ? "thumbtack-slash" : "thumbtack-outline"} size={14} />
                              {post.pinned ? "Unpin" : "Pin"}
                            </DropdownMenuItem>
                          )}
                          {!isPostAuthor && (
                            <DropdownMenuItem
                              onClick={() => setShowReportDialog(true)}
                            >
                              <Icon name="flag" size={14} />
                              Report
                            </DropdownMenuItem>
                          )}
                          {canDelete && (
                            <DropdownMenuItem
                              destructive
                              onClick={() => setShowDeleteDialog(true)}
                            >
                              <Icon name="trash" size={14} />
                              Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>

                  {/* Post body with rich text */}
                  {post.body_text && (
                    <RichBodyText
                      text={post.body_text}
                      mentionMap={mentionMap}
                      className="mb-3"
                    />
                  )}

                  {/* GIF preview */}
                  {showGifPreview && firstUrl && (
                    <div className="mb-3">
                      <GifPreview url={firstUrl} />
                    </div>
                  )}

                  {/* Link preview */}
                  {showLinkPreview && firstUrl && (
                    <div className="mb-3">
                      <LinkPreview url={firstUrl} />
                    </div>
                  )}

                  {/* Poll display */}
                  {post.poll && (
                    <div className="mb-3">
                      <PollDisplay
                        poll={post.poll}
                        onVote={handlePollVote}
                        isVoting={isVoting}
                        currentUserId={currentUserId}
                      />
                    </div>
                  )}

                  {/* Media preview - clickable, uses actual aspect ratio */}
                  {(hasImage || hasVideo) && imageUrl && (
                    <div
                      className="mb-3 rounded-xl overflow-hidden bg-surface-2 relative cursor-pointer"
                      onClickCapture={handleMediaClick}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleMediaClick(e as unknown as React.MouseEvent);
                        }
                      }}
                      style={{
                        boxShadow: "inset 0 0 0 1px var(--color-border-tertiary)",
                      }}
                    >
                      <img
                        src={imageUrl}
                        alt=""
                        className="w-full block"
                        style={{
                          aspectRatio:
                            firstMedia?.width && firstMedia?.height
                              ? `${firstMedia.width} / ${firstMedia.height}`
                              : undefined,
                        }}
                      />
                      {hasVideo && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                            <Icon name="play" size={20} className="text-text-primary ml-0.5" />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Location display */}
                  {post.location_object && (
                    <div className="mb-3">
                      <LocationView
                        location={post.location_object}
                        locationName={post.location_name}
                      />
                    </div>
                  )}

                  {/* Engagement stats */}
                  <div className="flex items-center gap-4 text-body-3 text-text-tertiary">
                    {/* Like button */}
                    <button
                      onClick={handleLikeToggle}
                      disabled={!currentUserId}
                      className={cn(
                        "flex items-center gap-1.5 transition-colors",
                        currentUserId && "hover:text-red"
                      )}
                    >
                      <Icon
                        name={post.liked ? "heart" : "heart-outline"}
                        size={20}
                        className={post.liked ? "text-red" : ""}
                      />
                      <span>{post.like_count || 0}</span>
                    </button>

                    {/* Comment button — scrolls to reply input */}
                    {post.allow_comments && (
                      <button
                        onClick={() => {
                          replyInputRef.current?.scrollIntoView({ behavior: "smooth" });
                          setIsInputExpanded(true);
                        }}
                        className="flex items-center gap-1.5 hover:text-text-secondary transition-colors"
                      >
                        <Icon name="comment-outline" size={20} />
                        <span>{post.comment_count || 0}</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Comments Section */}
            {post.allow_comments && (
              <div className="bg-surface-1 rounded-lg border border-border-tertiary">
                {/* Comments list */}
                <div className="p-4 space-y-4">
                  {commentsLoading && <CommentsSkeleton />}

                  {!commentsLoading && comments?.length === 0 && (
                    <p className="text-body-3 text-text-tertiary text-center py-4">
                      No comments yet. Be the first to comment!
                    </p>
                  )}

                  {!commentsLoading &&
                    (comments || []).map((comment) => (
                      <CommentItem
                        key={comment.id}
                        comment={comment}
                        author={comment.user_id ? profileMap.get(comment.user_id) : null}
                        postAuthorId={post?.author_id}
                        postAuthorProfile={post?.author_id ? profileMap.get(post.author_id) : null}
                        currentUserId={currentUserId}
                        userRole={userRole}
                        postId={postId}
                        mentionMap={mentionMap}
                        onLike={handleCommentLike}
                        canLike={!!currentUserId}
                      />
                    ))}
                </div>

                {/* Comment input */}
                <div ref={replyInputRef} className="p-4 border-t border-border-tertiary">
                  {!isInputExpanded ? (
                    <button
                      onClick={() => setIsInputExpanded(true)}
                      className="w-full flex items-center gap-2 px-4 py-2.5 rounded-2xl border border-border-tertiary bg-transparent hover:bg-surface-2 transition-colors"
                    >
                      <div
                        className="rounded-full overflow-hidden bg-surface-2 flex-shrink-0"
                        style={{ width: 24, height: 24 }}
                      >
                        {currentUserAvatar ? (
                          <img
                            src={currentUserAvatar}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-[10px] font-medium text-text-secondary">
                            {currentUserProfile
                              ? getInitials(userDisplayName(currentUserProfile))
                              : ""}
                          </div>
                        )}
                      </div>
                      <span className="text-body-2 text-text-tertiary flex-1 text-left">Reply?</span>
                      <button
                        className={cn(
                          "flex items-center justify-center transition-colors",
                          post.liked ? "text-red" : "text-text-tertiary hover:text-red"
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLikeToggle(e);
                        }}
                      >
                        <Icon name={post.liked ? "heart" : "heart-outline"} size={20} />
                      </button>
                    </button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <MentionTextarea
                        value={commentText}
                        onChange={setCommentText}
                        clubId={clubId}
                        currentUserId={currentUserId || ""}
                        panelPosition="above"
                        contextPostId={postId}
                        placeholder="Write a comment..."
                        className="w-full bg-surface-2 rounded-2xl px-4 py-3 text-body-2 text-text-primary placeholder:text-text-tertiary resize-none focus:outline-none border border-border-tertiary min-h-[44px]"
                        rows={1}
                        autoFocus
                        containerClassName="flex-1"
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey && !e.defaultPrevented) {
                            e.preventDefault();
                            handleSubmitComment();
                          }
                          if (e.key === "Escape" && !e.defaultPrevented) {
                            setCommentText("");
                            setIsInputExpanded(false);
                          }
                        }}
                      />
                      <button
                        onClick={handleSubmitComment}
                        disabled={!commentText.trim() || isSubmitting}
                        className={cn(
                          "flex-shrink-0 transition-colors",
                          commentText.trim() && !isSubmitting
                            ? "text-text-primary"
                            : "text-text-tertiary"
                        )}
                      >
                        <Icon name="circle-arrow-up" size={32} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Comments disabled message */}
            {!post.allow_comments && (
              <div className="bg-surface-1 rounded-lg border border-border-tertiary p-6 text-center">
                <p className="text-body-3 text-text-tertiary">
                  Comments are disabled for this post.
                </p>
              </div>
            )}
          </div>
        </div>
      </PageTransition>

      {/* Media viewer modal */}
      {viewerMedia && (
        <WebMediaViewer
          src={viewerMedia.src}
          type={viewerMedia.type}
          onClose={() => setViewerMedia(null)}
        />
      )}

      {/* Delete confirmation dialog */}
      <DeleteConfirmDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title="Delete post"
        description="Are you sure you want to delete this post? This action cannot be undone."
        isDeleting={isDeleting}
        onConfirm={handleDelete}
      />

      {/* Report content dialog */}
      <ReportContentDialog
        open={showReportDialog}
        onOpenChange={setShowReportDialog}
        itemId={postId}
        itemType="post"
      />
    </AppLayout>
  );
}

// Comment item component
interface CommentItemProps {
  comment: Comment;
  author: UserProfile | null | undefined;
  postAuthorId?: string | null;
  postAuthorProfile?: UserProfile | null;
  currentUserId?: string | null;
  userRole?: ClubRole;
  postId: string;
  mentionMap?: MentionMap;
  onLike?: (commentId: string) => void;
  canLike?: boolean;
}

function CommentItem({
  comment,
  author,
  postAuthorId,
  postAuthorProfile,
  currentUserId,
  userRole,
  postId,
  mentionMap,
  onLike,
  canLike = false,
}: CommentItemProps) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [deleteComment, { isLoading: isDeleting }] = useDeleteCommentMutation();

  const authorName = author ? userDisplayName(author) : "Unknown";
  const authorInitials = getInitials(authorName);
  const authorAvatar = author?.avatar_small || author?.avatar_medium;
  const formattedTime = formatRelativeTime(comment.created_at);

  // Check if commenter is the post author
  const isPostAuthor = postAuthorId && comment.user_id === postAuthorId;

  // Permission check for delete (comment author or club owner/cohost)
  const canDelete =
    currentUserId &&
    (currentUserId === comment.user_id || isOwnerOrCohost(userRole));

  // Get post author avatar for "liked by author" display
  const postAuthorAvatar = postAuthorProfile?.avatar_small || postAuthorProfile?.avatar_medium;
  const postAuthorInitials = postAuthorProfile ? getInitials(userDisplayName(postAuthorProfile)) : "?";

  const handleDelete = async () => {
    try {
      await deleteComment({ commentId: comment.id, postId }).unwrap();
      setShowDeleteDialog(false);
    } catch (error) {
      console.error("Failed to delete comment:", error);
    }
  };

  return (
    <div className="flex">
      {/* Left column - Avatar */}
      <div className="mr-2.5 flex-shrink-0">
        <div className="rounded-full overflow-hidden bg-surface-2" style={{ width: 32, height: 32 }}>
          {authorAvatar ? (
            <img src={authorAvatar} alt={authorName} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-xs font-medium text-text-secondary">
              {authorInitials}
            </div>
          )}
        </div>
      </div>

      {/* Right column - Content */}
      <div className="flex-1 min-w-0">
        {/* Header row with name, time, author indicator, liked by author, and overflow menu */}
        <div className="flex items-center gap-1.5 text-body-3">
          <span className="font-medium text-text-primary">{authorName}</span>
          <span className="text-text-tertiary">
            {formattedTime}
            {isPostAuthor && " • Author"}
          </span>

          {/* Spacer to push right-aligned items */}
          <div className="flex-1" />

          {/* Liked by author - show avatar with heart overlay (matches mobile) */}
          {comment.author_liked && postAuthorId && (
            <div className="relative mr-2" style={{ width: 16, height: 16 }}>
              <div className="rounded-full overflow-hidden bg-surface-2" style={{ width: 16, height: 16 }}>
                {postAuthorAvatar ? (
                  <img src={postAuthorAvatar} alt="Author" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[8px] font-medium text-text-secondary">
                    {postAuthorInitials}
                  </div>
                )}
              </div>
              {/* Micro heart overlay */}
              <div
                className="absolute flex items-center justify-center bg-red rounded-full"
                style={{ width: 10, height: 10, bottom: -2, left: -4 }}
              >
                <Icon name="heart" size={6} className="text-white" />
              </div>
            </div>
          )}

          {/* Overflow menu */}
          {currentUserId && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="p-1 -m-1 text-text-tertiary hover:text-text-secondary transition-colors">
                  <Icon name="ellipsis" size={16} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {currentUserId !== comment.user_id && (
                  <DropdownMenuItem onClick={() => setShowReportDialog(true)}>
                    Report
                  </DropdownMenuItem>
                )}
                {canDelete && (
                  <DropdownMenuItem
                    className="text-red focus:text-red"
                    onClick={() => setShowDeleteDialog(true)}
                  >
                    Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Comment body */}
        <RichBodyText text={comment.body_text} mentionMap={mentionMap} className="mt-1" />

        {/* Like button */}
        <div className="flex items-center gap-4 mt-2">
          <button
            className={cn(
              "flex items-center gap-1 text-body-3 transition-colors",
              comment.liked ? "text-red" : "text-text-tertiary",
              canLike && !comment.liked && "hover:text-red"
            )}
            disabled={!canLike}
            onClick={() => onLike?.(comment.id)}
          >
            <Icon name={comment.liked ? "heart" : "heart-outline"} size={14} />
            {comment.like_count > 0 && <span>{comment.like_count}</span>}
          </button>
        </div>
      </div>

      {/* Delete confirmation dialog */}
      <DeleteConfirmDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title="Delete comment"
        description="Are you sure you want to delete this comment? This action cannot be undone."
        isDeleting={isDeleting}
        onConfirm={handleDelete}
      />

      {/* Report comment dialog */}
      <ReportContentDialog
        open={showReportDialog}
        onOpenChange={setShowReportDialog}
        itemId={comment.id}
        itemType="comment"
      />
    </div>
  );
}

// Skeleton components
function PostDetailSkeleton() {
  return (
    <div className="p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="bg-surface-1 rounded-lg border border-border-tertiary p-6">
          <div className="flex items-center gap-3 mb-4">
            <Skeleton className="w-10 h-10 rounded-full" />
            <div>
              <Skeleton className="h-5 w-32 mb-1" />
              <Skeleton className="h-4 w-20" />
            </div>
          </div>
          <Skeleton className="h-4 w-full mb-2" />
          <Skeleton className="h-4 w-3/4 mb-2" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      </div>
    </div>
  );
}

function CommentsSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex gap-3">
          <Skeleton className="w-8 h-8 rounded-full flex-shrink-0" />
          <div className="flex-1">
            <Skeleton className="h-4 w-24 mb-2" />
            <Skeleton className="h-4 w-full" />
          </div>
        </div>
      ))}
    </div>
  );
}

// Helper function to format relative time
function formatRelativeTime(dateString: string): string {
  const date = DateTime.fromISO(dateString);
  const now = DateTime.now();
  const diff = now.diff(date, ["days", "hours", "minutes"]);

  if (diff.days >= 7) {
    return date.toFormat("MMM d");
  } else if (diff.days >= 1) {
    return `${Math.floor(diff.days)}d`;
  } else if (diff.hours >= 1) {
    return `${Math.floor(diff.hours)}h`;
  } else if (diff.minutes >= 1) {
    return `${Math.floor(diff.minutes)}m`;
  } else {
    return "now";
  }
}

// Helper function to get initials
function getInitials(name: string): string {
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}
